import type { IntelligenceRequest } from '@shared/domain/intelligenceRequest/IntelligenceRequest';
import { IntelligenceRequestStatus } from '@shared/domain/intelligenceRequest/IntelligenceRequestStatus';
import { useApi } from '@shared/services/api';
import { useSnackbar } from '@shared/services/SnackbarService';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AskQuestionModal from '../../review-reader/components/AskQuestionModal';
import CreateIntelligenceRequestModal from '../../review-reader/components/CreateIntelligenceRequestModal';
import ViewConsultationRequestModal from '../components/ViewConsultationRequestModal';

interface Review {
  id: number;
  companyId?: number;
  companyName: string;
  userId?: number;
}

interface UseMenuActionsParams {
  consultationRequests: IntelligenceRequest[];
  myRequestsToThisUser?: IntelligenceRequest[];
  openMessage: (config: any) => void;
  closeMessage: () => void;
  refetchUserData?: () => Promise<void>;
}

export function useMenuActions({
  consultationRequests,
  myRequestsToThisUser = [],
  openMessage,
  closeMessage,
  refetchUserData,
}: UseMenuActionsParams) {
  const navigate = useNavigate();
  const api = useApi();
  const { showSnackbar } = useSnackbar();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [_selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  const [callRequestAnchorEl, setCallRequestAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCallRequestId, setSelectedCallRequestId] = useState<number | null>(null);
  const [selectedCallRequestStatus, setSelectedCallRequestStatus] = useState<string | undefined>(undefined);

  const handleReviewMenuOpen = (event: React.MouseEvent<HTMLElement>, reviewId: number, reviews: Review[]) => {
    const review = reviews.find((r) => r.id === reviewId);
    setAnchorEl(event.currentTarget);
    setSelectedReviewId(reviewId);
    setSelectedReview(review || null);
  };

  const handleReviewMenuClose = () => {
    setAnchorEl(null);
    setSelectedReviewId(null);
    setSelectedReview(null);
  };

  const handleReadReview = () => {
    handleReviewMenuClose();

    if (!selectedReview) {
      return;
    }

    navigate(`/review-reader/${encodeURIComponent(selectedReview.companyName)}/${selectedReview.id}`);
  };

  const handleRequestConsultation = () => {
    if (!selectedReview) {
      handleReviewMenuClose();
      return;
    }

    const { id: reviewId, companyId, userId: authorId } = selectedReview;

    openMessage({
      modalType: 'action',
      header: 'Request Consultation',
      customBody: <CreateIntelligenceRequestModal targetUserId={authorId} companyId={companyId} reviewId={reviewId} />,
      size: 'large',
    });

    handleReviewMenuClose();
  };

  const handleAskQuestion = () => {
    if (!selectedReview) {
      handleReviewMenuClose();
      return;
    }

    const { id: reviewId, companyName } = selectedReview;

    let questionText = '';
    let isQuestionValid = false;
    let setShowValidation: ((show: boolean) => void) | null = null;

    openMessage({
      modalType: 'action',
      header: 'Ask a Question',
      customBody: (
        <AskQuestionModal
          reviewId={reviewId}
          companyName={companyName}
          onSubmit={(question, showValidationSetter) => {
            questionText = question;
            isQuestionValid = question.trim().length >= 25;
            setShowValidation = showValidationSetter;
          }}
          onTextChange={(question, isValid, showValidationSetter) => {
            questionText = question;
            isQuestionValid = isValid;
            setShowValidation = showValidationSetter;
          }}
          onRequestCall={() => {
            closeMessage();
            handleRequestConsultation();
          }}
        />
      ),
      size: 'medium',
      confirmLabel: 'Ask Question',
      cancelLabel: 'Cancel',
      confirmButtonColor: 'primary',
      onConfirm: async () => {
        if (isQuestionValid && questionText.trim()) {
          try {
            await api.post('/question', {
              question: questionText.trim(),
              reviewId,
            });
            showSnackbar('Question sent successfully!', 'success');
            closeMessage();
            return true;
          } catch {
            showSnackbar('Failed to send question. Please try again.', 'error');
            return false;
          }
        }
        if (setShowValidation) {
          setShowValidation(true);
        }
        return false;
      },
    });

    handleReviewMenuClose();
  };

  const handleReviewAction = (action: string) => {
    if (action === 'read') {
      handleReadReview();
    } else if (action === 'askQuestion') {
      handleAskQuestion();
    } else if (action === 'requestConsultation') {
      handleRequestConsultation();
    } else {
      handleReviewMenuClose();
    }
  };

  const handleCallRequestMenuOpen = (event: React.MouseEvent<HTMLElement>, callRequestId: number) => {
    setCallRequestAnchorEl(event.currentTarget);
    setSelectedCallRequestId(callRequestId);

    // Find the request status
    const allRequests = [...consultationRequests, ...myRequestsToThisUser];
    const selectedRequest = allRequests.find((r) => Number(r.id) === callRequestId);
    setSelectedCallRequestStatus(selectedRequest?.status);
  };

  const handleCallRequestMenuClose = () => {
    setCallRequestAnchorEl(null);
    setSelectedCallRequestId(null);
    setSelectedCallRequestStatus(undefined);
  };

  const handleCallRequestAction = (action: string) => {
    if (action === 'view') {
      // Search in both incoming requests and outgoing requests
      const allRequests = [...consultationRequests, ...myRequestsToThisUser];
      const selectedRequest = allRequests.find((r) => Number(r.id) === selectedCallRequestId);

      if (selectedRequest) {
        openMessage({
          modalType: 'action',
          header: 'Consultation Request Details',
          customBody: (
            <ViewConsultationRequestModal intelligenceRequest={selectedRequest} onRefetch={refetchUserData} />
          ),
          size: 'large',
        });
      }

      handleCallRequestMenuClose();
    } else if (action === 'accept') {
      // Accept the consultation request (author accepting)
      const selectedRequest = consultationRequests.find((r) => Number(r.id) === selectedCallRequestId);

      if (selectedRequest) {
        // Use the special accept endpoint which triggers notifications
        api
          .patch(`/intelligence-request/${selectedRequest.id}/accept-by-review-author`, {
            ...selectedRequest,
            status: IntelligenceRequestStatus.ACCEPTED_BY_REVIEW_AUTHOR,
          })
          .then(() => {
            showSnackbar('Consultation accepted successfully', 'success');
            if (refetchUserData) {
              refetchUserData();
            }
          })
          .catch(() => {
            showSnackbar('Failed to accept request. Please try again.', 'error');
          });
      }

      handleCallRequestMenuClose();
    } else if (action === 'cancel' || action === 'decline') {
      // Find the request to cancel/decline
      const allRequests = [...consultationRequests, ...myRequestsToThisUser];
      const selectedRequest = allRequests.find((r) => Number(r.id) === selectedCallRequestId);

      if (selectedRequest) {
        // Determine the status based on action
        // 'cancel' = requester cancelling their own request -> CANCELLED
        // 'decline' = author declining incoming request -> REJECTED
        const newStatus =
          action === 'cancel' ? IntelligenceRequestStatus.CANCELLED : IntelligenceRequestStatus.REJECTED;

        const successMessage =
          action === 'cancel' ? 'Request cancelled successfully' : 'Consultation declined successfully';

        // Call API directly and show snackbar
        api
          .patch(`/intelligence-request/${selectedRequest.id}`, {
            ...selectedRequest,
            status: newStatus,
          })
          .then(() => {
            showSnackbar(successMessage, 'success');
            if (refetchUserData) {
              refetchUserData();
            }
          })
          .catch(() => {
            showSnackbar('Failed to update request. Please try again.', 'error');
          });
      }

      handleCallRequestMenuClose();
    } else {
      handleCallRequestMenuClose();
    }
  };

  return {
    reviewMenuAnchor: anchorEl,
    handleReviewMenuOpen,
    handleReviewMenuClose,
    handleReviewAction,
    callRequestMenuAnchor: callRequestAnchorEl,
    handleCallRequestMenuOpen,
    handleCallRequestMenuClose,
    handleCallRequestAction,
    selectedCallRequestStatus,
  };
}
